/**
 * Экран режимов «Скорость» и «Точность».
 *
 * Пользователь видит, что от него требуется прямо сейчас: какой темп нужно
 * взять или что элемент засчитывается только без единой ошибки, — и чем
 * закончился предыдущий прогон. Молчаливая лестница выглядела бы случайной
 * (ТЗ §6, режимы 6 и 7).
 *
 * Цель и вердикт стоят над текстом: без них прогон непонятен. Правило
 * лестницы — под ним (ТЗ FR-EN-84).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Card } from '@type2x/ui';
import { currentDrill, type TempoSession } from '@type2x/feature-tempo';

import type { Runtime } from '../runtime.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface TempoScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly session: TempoSession;
  /** Идентификатор всего занятия: прогоны — его части, а не отдельные сессии. */
  readonly sessionId: SessionId;
  readonly onRunDone: (run: TypingSession) => void;
}

export function TempoScreen({
  runtime,
  t,
  session,
  sessionId,
  onRunDone,
}: TempoScreenProps): React.JSX.Element {
  const title = t(`tempo.title.${session.mode}`);
  const drill = currentDrill(session);
  if (drill === null) return <Card title={title}>{t('tempo.done')}</Card>;

  const done = session.drills.filter((item) => item.cleared || item.deferred).length;

  return (
    <DrillLayout
      t={t}
      title={title}
      now={
        <>
          <span className="t2x-muted">
            {t(`tempo.goal.${session.mode}`, { cpm: session.targetCpm })}
          </span>
          {session.lastVerdict !== null && (
            <span className="t2x-verdict">{t(`tempo.verdict.${session.lastVerdict}`)}</span>
          )}
        </>
      }
      progress={{
        value: session.drills.length === 0 ? 0 : done / session.drills.length,
        label: t('tempo.progress'),
      }}
      details={
        <>
          <p className="t2x-muted">{t('tempo.hint', { cpm: session.targetCpm })}</p>
          <p className="t2x-muted">{t(`tempo.stage.${session.mode}`)}</p>
        </>
      }
    >
      <TypingScreen
        // Ключ включает номер элемента и попытки: повтор того же элемента —
        // новый прогон, а не продолжение прежнего.
        key={`${session.currentIndex}:${drill.attempts}`}
        runtime={runtime}
        t={t}
        title={t('tempo.typing')}
        hint={t(`tempo.stage.${session.mode}`)}
        sessionId={sessionId}
        layoutId={session.layoutId}
        targetText={drill.text}
        hintLevel={session.hintLevel}
        compact
        onFinished={onRunDone}
      />
    </DrillLayout>
  );
}
